import * as Popover from '@radix-ui/react-popover';
import { Hint, HintType } from '@sparx/api/apis/sparx/hints/v1/hints';
import { TaskItemCompletion } from '@sparx/api/apis/sparx/packages/v1/spxpkg';
import {
  Activity,
  ActivityActionResponse,
  GapEvaluation,
  QuestionActivity,
} from '@sparx/api/sparxweb/swmsg/sparxweb';
import { Button, HiddenAt } from '@sparx/design/components';
import { HintInfo, IInput, IStep, SparxQuestion } from '@sparx/question';
import { ChevronLeft } from '@sparx/sparx-design/icons';
import { KeyboardShortcuts } from 'app/KeyboardShortcuts';
import { useKeyboardMode } from 'context/keyboardmode';
import { useTutorialSpotlight } from 'context/tutorialspotlight';
import { motion } from 'motion/react';
import { useMemo, useRef, useState } from 'react';
import { useAnalytics } from 'utils/analytics';
import { useFeatureFlags } from 'utils/feature-flags';
import { GetCalculatorAllowed } from 'utils/question';
import { getAssetUrl } from 'utils/urls';
import { useLastUndefinedValue } from 'utils/useLastUndefinedValue';
import { hasInputForRefsAnswerPart, resetInputForRefs } from 'views/answer/utils';
import { getQuestionColours, getQuestionFont } from 'views/question/getQuestionParams';
import { QuestionAnswerLayout } from 'views/question/QuestionAnswerLayout';
import { QuestionInfo } from 'views/question/QuestionInfo';

import BottomBar from '../lqd/bottom-bar';
import styles from '../question/Question.module.css';
import { Result } from '../result';
import { HintModal } from './hints/HintModal';
import { HintTooltip } from './hints/HintTooltip';

interface IAnswerProps {
  activity: Activity;
  question: QuestionActivity;
  input: IInput;
  layout: IStep;
  setInput: (input: IInput) => void;
  goToQuestion: () => void;
  submitAnswer?: () => void;
  submitAnswerResponse?: ActivityActionResponse;
  submitError?: boolean;
  isLastItem: boolean;
  isTaskComplete: boolean;
  numSwapsUsed: number;
  currentTaskItemPath: string;
  nextTaskItemPath: string;
  isItemOnNonInitialChance: boolean;
  retryTask: () => void;
  hideBookworkCodes: boolean;
  onScaleChange?: (value: number) => void;

  // temporary data for analytics on disabled submit button clicks
  disabledSubmitAnswerAnalytics?: Record<string, boolean | string | number>;
  firstChanceGapEvaluationsRef?: React.MutableRefObject<Record<string, GapEvaluation> | undefined>;
  currentTaskItem?: TaskItemCompletion;
  placeholderMap?: Dictionary<string, string>;
  // an array containing an entry for each part in the question. Each entry is an array of refs in
  // that part
  partRefs: string[][];
}

export const Answer = ({
  activity,
  question,
  input,
  layout,
  setInput,
  goToQuestion,
  submitAnswer,
  submitAnswerResponse: submitAnswerResponseInput,
  submitError = false,
  isLastItem,
  isTaskComplete,
  numSwapsUsed,
  nextTaskItemPath,
  currentTaskItemPath,
  isItemOnNonInitialChance,
  retryTask,
  hideBookworkCodes,
  onScaleChange,
  firstChanceGapEvaluationsRef,
  currentTaskItem,
  placeholderMap,
  partRefs,
}: IAnswerProps) => {
  const [hintForModal, setHintForModal] = useState<Hint | undefined>(undefined);
  const { enabled: isKeyboardMode } = useKeyboardMode();
  const { tutorialIsShowing } = useTutorialSpotlight();
  const featureFlags = useFeatureFlags();

  // cache the submit answer response as if a hint is given we'll be passed undefined while the new
  // response is being fetched, and that makes the UI flicker
  const submitAnswerResponse = useLastUndefinedValue(submitAnswerResponseInput);

  const multiPartMarkingEnabled =
    submitAnswerResponse?.gapEvaluations &&
    Object.keys(submitAnswerResponse.gapEvaluations).length > 0 &&
    featureFlags.getBooleanFlag('sparxweb2-enable-mpa', false);
  const examDisplay = featureFlags.getBooleanFlag('sparxweb2-exam-display', false);

  const hintGiven = !!submitAnswerResponse?.hint && submitAnswerResponse.status === 'ACTIVE';

  const hintInfo: HintInfo | undefined = useMemo(() => {
    const hint = submitAnswerResponse?.hint;
    const activityIsActive =
      submitAnswerResponse?.status === 'ACTIVE' || submitAnswerResponse?.status === undefined;

    if (!activityIsActive) {
      return undefined;
    }

    if (!hint || !placeholderMap) {
      return undefined;
    }

    const partHasInputMap: Partial<Record<string, boolean>> = {};
    hint.inputRefs.forEach(ref => {
      partHasInputMap[ref] = hasInputForRefsAnswerPart(ref, input, partRefs);
    });

    const hintInfo: HintInfo = {
      inputPlaceholderMap: placeholderMap,
      hint: hint,
      onReset: () => {
        setInput(resetInputForRefs(hint.inputRefs, input));
      },
      partHasInputMap: partHasInputMap,
    };
    return hintInfo;
  }, [input, partRefs, placeholderMap, setInput, submitAnswerResponse]);

  // If an answer has been submitted and no hint has been given, the activity has been answered and cannot
  // be reattempted
  const activityAnswered = submitAnswerResponse && !hintGiven;

  const sendEvent = useAnalytics();
  const keyboardEnterFunc = (ev: KeyboardEvent) => {
    if (tutorialIsShowing) {
      return;
    }
    if (submitAnswer !== undefined && (submitAnswerResponse === undefined || hintGiven)) {
      // prevent default when pressing enter to submit. This prevents the enter key from triggering
      // the onClick behavior of the last (if still focussed) button on the keypad
      ev.preventDefault();
      submitAnswer();
      // BOOL-3095 - page event removed to reduce CloudFlare traffic
      // sendEvent({ action: 'pressed enter to submit answer', category: 'lqd' });
    }
  };

  const firstEmptyInputRef = useRef<HTMLElement | null>(null);

  const answerContent = (
    <>
      <KeyboardShortcuts enterFunc={keyboardEnterFunc} source="answer" />
      <QuestionInfo
        bookworkCode={hideBookworkCodes ? undefined : question.bookworkCode}
        calculatorAllowed={GetCalculatorAllowed(layout)}
        showBanner={false}
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={styles.QuestionWrapper}
      >
        <SparxQuestion
          layout={layout.layout}
          input={input}
          setInput={setInput}
          mode="answer"
          readOnly={!!activityAnswered}
          colours={getQuestionColours(examDisplay)}
          font={getQuestionFont(examDisplay)}
          centered={true}
          shuffleSeed={`${activity.activityIndex}`}
          keyboardMode={isKeyboardMode}
          firstEmptyInputRef={firstEmptyInputRef}
          onScaleChange={onScaleChange}
          gapEvaluations={multiPartMarkingEnabled ? submitAnswerResponse.gapEvaluations : undefined}
          questionMarkingMode="part"
          firstChanceGapEvaluationsRef={firstChanceGapEvaluationsRef}
          sendAnalyticEvent={(action, labels) =>
            sendEvent({ category: 'question', action, labels })
          }
          getAssetUrl={getAssetUrl}
          hintInfo={hintInfo}
          numHints={submitAnswerResponse?.numHintsGiven}
          triggerHintModal={setHintForModal}
          returnToQuestion={goToQuestion}
        />
      </motion.div>
    </>
  );

  const bottomBarAndResult =
    submitAnswerResponse && !hintGiven ? (
      <Result
        submitAnswerResponse={submitAnswerResponse}
        isLastItem={isLastItem}
        isTaskComplete={isTaskComplete}
        currentTaskItemPath={currentTaskItemPath}
        nextTaskItemPath={nextTaskItemPath}
        retryTask={retryTask}
        isItemInSecondChance={isItemOnNonInitialChance}
        numSwapsUsed={numSwapsUsed}
        currentTaskItem={currentTaskItem}
      />
    ) : (
      <BottomBar>
        <Button
          variant="secondary"
          onClick={() => {
            // BOOL-3095 - page event removed to reduce CloudFlare traffic
            // sendEvent({ action: 'clicked back from answer', category: 'lqd' });
            goToQuestion();
          }}
          leftIcon={<ChevronLeft />}
        >
          Back
        </Button>
        <div />
        <Popover.Root open={submitError}>
          <Popover.Anchor>
            <HintTooltip
              hintType={hintInfo?.hint.hintType || HintType.HINT_UNSPECIFIED}
              disabled={!hintGiven || submitAnswer !== undefined}
            >
              <Button
                variant="primary"
                onClick={() => {
                  // BOOL-3095 - page event removed to reduce CloudFlare traffic
                  // sendEvent({ action: 'clicked submit answer', category: 'lqd' });
                  submitAnswer?.();
                }}
                onDisabledClick={() => {
                  if (firstEmptyInputRef.current) {
                    firstEmptyInputRef.current.scrollIntoView({
                      block: 'start',
                      behavior: 'smooth',
                    });
                  }
                  // BOOL-3095 - page event removed to reduce CloudFlare traffic
                  // sendEvent({
                  //   action: 'clicked disabled submit answer',
                  //   category: 'lqd',
                  //   labels: {
                  //     'answer fields': JSON.stringify(input),
                  //     ...disabledSubmitAnswerAnalytics,
                  //   },
                  // });
                }}
                isDisabled={submitAnswer === undefined}
              >
                Submit
                <HiddenAt as="span" breakpoint="sm">
                  {' '}
                  answer
                </HiddenAt>
              </Button>
            </HintTooltip>
          </Popover.Anchor>
          <Popover.Portal>
            <Popover.Content className={styles.SubmitErrorContent}>
              <p className={styles.ErrorMessage}>
                <strong>Something went wrong.</strong>
                <span> Please make sure you have written down your answer and </span>
                <a className={styles.RefreshLink} href="">
                  refresh the page
                </a>
              </p>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </BottomBar>
    );

  return (
    <>
      <QuestionAnswerLayout content={answerContent} bottomBar={bottomBarAndResult} />
      <HintModal
        onOpenChange={open => {
          if (!open) {
            setHintForModal(undefined);
          }
        }}
        hint={hintForModal}
      />
    </>
  );
};
